<template>
  <div class="benefit-orders">
    <h2>权益订单管理</h2>
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <span>权益订单列表</span>
        </div>
      </template>
      <div class="order-list-content">
        <!-- 搜索区域 -->
        <div class="search-area">
          <el-input
            v-model="searchForm.keyword"
            placeholder="请输入订单编号、用户昵称、手机号码、商品名称"
            prefix-icon="Search"
            clearable
            class="search-input"
          >
          </el-input>
          <div class="search-filters">
            <el-select v-model="searchForm.status" placeholder="订单状态" clearable>
              <el-option label="待付款" value="pending" />
              <el-option label="待核销" value="paid" />
              <el-option label="已完成" value="completed" />
              <el-option label="已取消" value="cancelled" />
              <el-option label="已退款" value="refunded" />
            </el-select>
            <el-date-picker
              v-model="searchForm.dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              class="date-picker"
            />
            <el-button type="primary" @click="handleSearch">
              <el-icon><Search /></el-icon>
              搜索
            </el-button>
          </div>
        </div>

        <!-- 数据表格 -->
        <el-table
          v-loading="loading"
          :data="orderList"
          stripe
          style="width: 100%"
          class="order-table"
          :row-key="row => row.id"
        >
          <el-table-column label="订单信息" width="400">
            <template #default="scope">
              <div>
                <div><strong>订单编号：</strong>{{ scope.row.order_no }}</div>
                <div><strong>兑换时间：</strong>{{ formatDateTime(null, null, scope.row.created_at) }}</div>
                <div>
                  <strong>状态：</strong>
                  <el-tag :type="getStatusTagType(scope.row.status)">
                    {{ getStatusText(scope.row.status) }}
                  </el-tag>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="兑换人信息" width="200">
            <template #default="scope">
              <div>
                <div>昵称：{{ scope.row.user?.nickname || '-' }}</div>
                <div>手机：{{ scope.row.user?.phone || '-' }}</div>
                <div>会员等级：{{ scope.row.user?.member_level?.level_name || scope.row.user?.member_level || '-' }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="商品信息" width="250">
            <template #default="scope">
              <div class="product-title">
                <template v-for="(item, index) in scope.row.items" :key="index">
                  {{ item.product_name }}{{ index < scope.row.items.length - 1 ? ' ' : '' }}
                </template>
                <template v-if="!scope.row.items || scope.row.items.length === 0">-</template>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="支付信息" width="180">
            <template #default="scope">
              <div>
                <div>实付款：{{ scope.row.total_amount }} 元</div>
                <div>{{ scope.row.points_used }} 积分</div>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="250" fixed="right">
            <template #default="scope">
              <el-button type="primary" size="small" @click="showOrderDetail(scope.row)">
                <el-icon><View /></el-icon>
                详情
              </el-button>
              <el-button
                v-if="scope.row.refund_status === 'applying'"
                type="danger"
                size="small"
                @click="showRefundDialog(scope.row)"
              >
                <el-icon><Money /></el-icon>
                退款
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-area">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 订单详情弹窗 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="订单详情"
      width="60%"
    >
      <div v-if="currentOrder" class="order-detail-content">
        <el-descriptions title="基本信息" :column="2" border>
          <el-descriptions-item label="订单编号">{{ currentOrder.order_no }}</el-descriptions-item>
          <el-descriptions-item label="订单状态">
            <el-tag :type="getStatusTagType(currentOrder.status)">
              {{ getStatusText(currentOrder.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="兑换时间">{{ formatDateTime(null, null, currentOrder.created_at) }}</el-descriptions-item>
          <el-descriptions-item label="支付金额">{{ currentOrder.total_amount }} 元</el-descriptions-item>
          <el-descriptions-item label="支付积分">{{ currentOrder.points_used }} 积分</el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="用户信息" :column="2" border style="margin-top: 20px">
          <el-descriptions-item label="用户昵称">{{ currentOrder.user?.nickname }}</el-descriptions-item>
          <el-descriptions-item label="手机号码">{{ currentOrder.user?.phone }}</el-descriptions-item>
          <el-descriptions-item label="会员等级">{{ currentOrder.user?.member_level?.level_name || currentOrder.user?.member_level || '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="商品信息" :column="1" border style="margin-top: 20px">
          <el-descriptions-item v-for="(item, index) in currentOrder.items" :key="index" :label="`商品 ${index + 1}`">
            <div class="product-item">
              <el-image
                :src="item.product_image || ''"
                fit="cover"
                style="width: 80px; height: 80px; margin-right: 15px"
              >
                <template #error>
                  <div class="image-error">
                    <el-icon><Picture /></el-icon>
                    <span>无图片</span>
                  </div>
                </template>
              </el-image>
              <div class="product-details">
                <div class="product-name">{{ item.product_name }}</div>
                <div class="product-sku" v-if="item.sku_name">{{ item.sku_name }}</div>
                <div class="product-intro" v-if="item.product_intro">{{ item.product_intro }}</div>
                <div class="product-quantity">数量：{{ item.quantity }}</div>
                <div class="product-price">单价：{{ item.unit_price }} 元</div>
                <div class="product-subtotal">小计：{{ item.subtotal }} 元</div>
              </div>
            </div>
          </el-descriptions-item>
          <el-descriptions-item v-if="!currentOrder.items || currentOrder.items.length === 0" label="商品信息">
            无商品信息
          </el-descriptions-item>
        </el-descriptions>

        <el-descriptions title="兑换码信息" :column="2" border style="margin-top: 20px" v-if="currentOrder.code">
          <el-descriptions-item label="兑换码">
            <span  class="code-text">{{ currentOrder.code }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="过期时间">{{ currentOrder.code_expired_at ? formatDateTime(null, null, currentOrder.code_expired_at) : '-' }}</el-descriptions-item>
        </el-descriptions>

        <el-descriptions v-if="currentOrder.refund_status && currentOrder.refund_status !== 'none'" title="退款信息" :column="2" border style="margin-top: 20px">
          <el-descriptions-item label="退款状态">
            <el-tag :type="getRefundStatusTagType(currentOrder.refund_status)">
              {{ getRefundStatusText(currentOrder.refund_status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="退款原因">{{ currentOrder.refund_reason || '无' }}</el-descriptions-item>
          <el-descriptions-item label="退款时间">{{ currentOrder.refunded_at ? formatDateTime(null, null, currentOrder.refunded_at) : '-' }}</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>

    <!-- 退款处理弹窗 -->
    <el-dialog
      v-model="refundDialogVisible"
      title="退款处理"
      width="50%"
    >
      <el-form :model="refundForm" label-width="120px">
        <el-form-item label="订单编号">
          <span>{{ currentOrder?.order_no }}</span>
        </el-form-item>
        <el-form-item label="退款金额">
          <span>{{ currentOrder?.total_amount }} 元</span>
        </el-form-item>
        <el-form-item label="退还积分">
          <span>{{ currentOrder?.points_used }} 积分</span>
        </el-form-item>
        <el-form-item label="退款原因">
          <span>{{ currentOrder?.refund_reason || '用户申请退款' }}</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="refundDialogVisible = false">取消</el-button>
        <el-button type="danger" @click="handleRefund(true)">确认退款</el-button>
        <el-button type="info" @click="handleRefund(false)">拒绝退款</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, View, Money, Picture } from '@element-plus/icons-vue';
import * as orderApi from '../../api/orders';

export default {
  name: 'BenefitOrders',
  components: {
    Search,
    View,
    Money,
    Picture
  },
  setup() {
    const searchForm = reactive({
      keyword: '',
      status: '',
      dateRange: []
    });

    const loading = ref(false);
    const orderList = ref([]);
    const pagination = reactive({
      currentPage: 1,
      pageSize: 10,
      total: 0
    });

    const detailDialogVisible = ref(false);
    const currentOrder = ref(null);

    const refundDialogVisible = ref(false);
    const refundForm = reactive({
      approve: true,
      reason: ''
    });

    const getStatusText = (status) => {
      const statusMap = {
        pending: '待付款',
        pending_payment: '待付款',
        paid: '待核销',
        shipped: '待签收',
        completed: '已完成',
        done: '已完成',
        cancelled: '已取消',
        refunded: '已退款'
      };
      return statusMap[status] || status;
    };

    const getStatusTagType = (status) => {
      const typeMap = {
        pending: 'info',
        pending_payment: 'info',
        paid: 'warning',
        shipped: 'primary',
        completed: 'success',
        done: 'success',
        cancelled: 'info',
        refunded: 'danger'
      };
      return typeMap[status] || 'info';
    };

    const getRefundStatusText = (status) => {
      const statusMap = {
        none: '无',
        applying: '申请中',
        refunded: '已退款',
        rejected: '已拒绝'
      };
      return statusMap[status] || status;
    };

    const getRefundStatusTagType = (status) => {
      const typeMap = {
        none: 'info',
        applying: 'warning',
        refunded: 'success',
        rejected: 'danger'
      };
      return typeMap[status] || 'info';
    };



    const loadOrders = async () => {
      loading.value = true;
      try {
        const params = {
          page: pagination.currentPage,
          per_page: pagination.pageSize,
          search: searchForm.keyword,
          status: searchForm.status,
          product_type: 'benefit'
        };
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.start_date = searchForm.dateRange[0];
          params.end_date = searchForm.dateRange[1];
        }
        console.log('发送请求参数:', params);
        const response = await orderApi.fetchOrdersList(params);
        console.log('API响应:', response);
        if (response.code === 200) {
          orderList.value = response.data.data;
          pagination.total = response.data.total;
          console.log('orderList:', orderList.value);
        } else {
          ElMessage.error('获取订单列表失败');
        }
      } catch (error) {
        ElMessage.error('获取订单列表失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    const handleSearch = () => {
      pagination.currentPage = 1;
      loadOrders();
    };

    const showOrderDetail = (order) => {
      currentOrder.value = order;
      detailDialogVisible.value = true;
    };

    const handleGenerateCode = async (order) => {
      try {
        const response = await orderApi.generateCode(order.id);
        if (response.code === 200) {
          ElMessage.success('兑换码生成成功');
          loadOrders();
          if (currentOrder.value && currentOrder.value.id === order.id) {
            currentOrder.value = response.data;
          }
        } else {
          ElMessage.error('生成兑换码失败');
        }
      } catch (error) {
        ElMessage.error('生成兑换码失败');
        console.error(error);
      }
    };

    const showRefundDialog = (order) => {
      currentOrder.value = order;
      refundDialogVisible.value = true;
    };

    const handleRefund = async (approve) => {
      try {
        const response = await orderApi.processRefund(currentOrder.value.id, {
          approve,
          reason: approve ? '退款处理' : '拒绝退款'
        });
        if (response.code === 200) {
          ElMessage.success(approve ? '退款处理成功' : '已拒绝退款');
          refundDialogVisible.value = false;
          loadOrders();
        } else {
          ElMessage.error('退款处理失败');
        }
      } catch (error) {
        ElMessage.error('退款处理失败');
        console.error(error);
      }
    };

    const handleSizeChange = (size) => {
      pagination.pageSize = size;
      loadOrders();
    };

    const handleCurrentChange = (current) => {
      pagination.currentPage = current;
      loadOrders();
    };

    // 格式化日期时间为ymdhis标准格式 (YYYY-MM-DD HH:mm:ss)
    const formatDateTime = (row, column, cellValue) => {
      if (!cellValue) return '';
      const date = new Date(cellValue);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    onMounted(() => {
      loadOrders();
    });

    return {
      searchForm,
      loading,
      orderList,
      pagination,
      detailDialogVisible,
      currentOrder,
      refundDialogVisible,
      refundForm,
      getStatusText,
      getStatusTagType,
      getRefundStatusText,
      getRefundStatusTagType,
      formatDateTime,
      handleSearch,
      showOrderDetail,
      handleGenerateCode,
      showRefundDialog,
      handleRefund,
      handleSizeChange,
      handleCurrentChange
    };
  }
};
</script>

<style scoped>
.benefit-orders {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-area {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.search-input {
  width: 450px;
}

.search-filters {
  display: flex;
  gap: 10px;
  align-items: center;
}

.date-picker {
  width: 300px;
}

.order-list-content {
  margin-top: 20px;
}

.order-table {
  margin-bottom: 20px;
}

.pagination-area {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.product-info {
  display: flex;
  align-items: center;
}

.product-detail {
  flex: 1;
}

.product-title {
  font-weight: bold;
  margin-bottom: 5px;
}

.product-intro {
  font-size: 12px;
  color: #999;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 150px;
}

.order-detail-content {
  max-height: 600px;
  overflow-y: auto;
}

.code-text {
  font-family: monospace;
  font-size: 16px;
  font-weight: bold;
  color: #409EFF;
  letter-spacing: 2px;
}

.product-item {
  display: flex;
  align-items: flex-start;
}

.product-details {
  flex: 1;
}

.product-name {
  font-weight: bold;
  margin-bottom: 5px;
}

.product-sku {
  font-size: 12px;
  color: #666;
  margin-bottom: 3px;
}

.product-intro {
  font-size: 12px;
  color: #999;
  margin-bottom: 3px;
  line-height: 1.4;
}

.product-quantity,
.product-price,
.product-subtotal {
  font-size: 12px;
  color: #666;
  margin-top: 3px;
}

.image-error {
  width: 80px;
  height: 80px;
  background-color: #f5f7fa;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid #ebeef5;
  border-radius: 4px;
}

.image-error .el-icon {
  font-size: 24px;
  color: #c0c4cc;
  margin-bottom: 4px;
}

.image-error span {
  font-size: 12px;
  color: #909399;
}
</style>